import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import './component.scss'
import {
  ADMIN_PROJECT_ID,
  COMMANDCENTER_URL,
  INNOMAINT_URL,
  INNOMAINT_URL_QR_CODE,
  REPORT_URL,
} from '../utils/constants'
import {
  APPOINTMENTS_READ,
  PATIENTS_READ,
  PATIENTS_WRITE,
  LMS_READ,
  IPD_READ,
  ADMINISTRATION_READ,
  RADIOLOGY_WRITE,
  MEMBERSHIP_READ,
  PACKAGES_READ,
  OPD_READ,
  CALENDAR_READ,
  SLOTS_READ,
  LMS_ADMIN_WRITE,
  REFERRED_OUT,
  RADIOLOGY_READ,
  APPS,
  CAMP_VIEW_READ,
  ADMIN_ROLES,
} from '../utils/roles'
import KeycloakService from '../utils/keycloakService'
import { startSxpProxy } from '../utils/api'
import { selectSelectedLocationId } from '../features/location/locationSlice'
import { useAppDispatch, useAppSelector } from '../app/hooks'
import {
  bedManagementOptions,
  labOptions,
  radiologyOptions,
} from '../features/lms/components/admin/AdminConfig'
import {
  setLmsItem,
  setLmsAdminSearch,
  setLmsFormMode,
  setSelectedValue,
} from '../features/lms/lmsSlice'
import {
  devicesOptions,
  intent,
  MainMenuConst,
  partnersOptions,
  subdropdownitems,
} from './constants'
import './MainMenu.css'
import {
  doctor,
  labDeviceModel,
  labDevices,
  parameterMappings,
  partners,
  pathologist,
  proRithmDashboard,
  radilogist,
  radiologyDevices,
} from '../features/bedManagement/endpoints/EndPoints'
interface MenuItem {
  name: string
  label: string
  link: any
  roles?: string[]
  dropdownItems?: DropdownItem[]
}

interface DropdownItem {
  name: string
  label: string
  link: any
  roles?: any
  onClick?: () => void
  subMenuItems?: DropdownItem[]
  isExternal?: boolean
}
interface SubdropdownRefs {
  [key: string]: HTMLDivElement | null
}

// Map menu items to their associated routes for active state handling
const MENU_ROUTE_MAPPINGS: Record<string, string[]> = {
  'OPD Patient View': [
    '/listOfCamps',
    '/opdPatientView',
    '/ecgList',
    '/pftList',
  ],
  // Add more mappings here as needed
  // Example: 'laboratory': ['/lms', '/lms/referred', '/lms/trimPdf'],
}

const MainMenu = () => {
  const dispatch = useAppDispatch()
  const location = useLocation()
  const [hoveredItem, setHoveredItem] = useState<string | null>(null)
  const [hoveredDropdownItem, setHoveredDropdownItem] = useState<string | null>(
    null
  )
  const [selectedDropdownValue, setSelectedDropdownValue] = useState<
    string | null
  >(null)
  const [BillingUrl, setBillingurl] = useState('')
  const [pharmacyUrl, setPharmacyUrl] = useState('')
  const locationId = useAppSelector(selectSelectedLocationId)
  const [dropdownPosition, setDropdownPosition] = useState<{
    top: number
    left: number
  }>({ top: 0, left: 0 })
  const fetchAuthLink = async (
    intent: string,
    location: string,
    setUrl: (url: string) => void
  ) => {
    try {
      const data = await startSxpProxy(ADMIN_PROJECT_ID, intent, { location })
      setUrl(data?.data?.auth_link || '')
    } catch (err) {
      console.error(err, 'err')
    }
  }

  useEffect(() => {
    fetchAuthLink(intent?.odooLogin, locationId, setBillingurl)
  }, [locationId])

  useEffect(() => {
    fetchAuthLink(intent?.pharmacyLocation, locationId, setPharmacyUrl)
  }, [locationId])

  const handleMouseEnter = (itemName: string, top: number, left: number) => {
    setHoveredItem(itemName)
    setDropdownPosition({ top, left })
  }

  const handleMouseLeave = () => {
    setHoveredItem(null)
    setHoveredDropdownItem(null)
  }

  const subdropdownRef = useRef<SubdropdownRefs>({})
  const handleDropdownItemEnter = (
    itemName: string,
    event: React.MouseEvent<HTMLElement>
  ) => {
    setHoveredDropdownItem(itemName)

    const ref = subdropdownRef.current[itemName]
    if (ref) {
      const dropdownItemRect = event?.currentTarget?.getBoundingClientRect()
      const dropdownRect = event?.currentTarget
        ?.closest('.location-dropdown')
        ?.getBoundingClientRect()

      if (dropdownRect) {
        const viewportWidth = window?.innerWidth
        const subDropdownWidth = ref?.getBoundingClientRect()?.width
        const dropdownItemRectRight = dropdownItemRect?.right
        const dropdownItemRectTop = dropdownItemRect?.top
        ref.style.position = 'absolute'
        ref.style.top = `${dropdownItemRectTop - dropdownRect.top}px`
        if (dropdownItemRectRight + subDropdownWidth > viewportWidth) {
          ref.style.left = `-${subDropdownWidth}px`
        } else {
          ref.style.left = `${dropdownItemRect.width}px`
        }
      }
    }
  }

  const handleDropdownItemLeave = () => {
    setHoveredDropdownItem(null)
  }
  const hasRole = (roles: string[]) => {
    return roles.every(() => KeycloakService.hasRole(roles))
  }
  const splitRadiology = hoveredDropdownItem
  const options = useMemo(() => {
    let opts: { value: string; label: string }[] = []

    if (splitRadiology === subdropdownitems?.radiology) {
      opts = [...radiologyOptions]
    } else if (splitRadiology === subdropdownitems?.bedManagementAdmin) {
      opts = bedManagementOptions
    } else if (splitRadiology == subdropdownitems?.devices) {
      opts = devicesOptions
    } else if (splitRadiology == subdropdownitems?.partners) {
      opts = partnersOptions
    } else {
      opts = [...labOptions]
    }
    return opts?.sort((a, b) => (a?.label < b?.label ? -1 : 1))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    splitRadiology,
    radiologyOptions,
    bedManagementOptions,
    labOptions,
    devicesOptions,
    partnersOptions,
  ])

  options?.sort?.((a, b) => (a?.label < b?.label ? -1 : 1))

  useEffect(() => {
    dispatch(setLmsAdminSearch(''))
  }, [dispatch, selectedDropdownValue, options])

  useEffect(() => {
    if (selectedDropdownValue) {
      dispatch(setLmsItem(selectedDropdownValue))
    }
  }, [dispatch, selectedDropdownValue])

  const handleChange = (ev: ChangeEvent<HTMLSelectElement>) => {
    const value = ev.target.value
    dispatch(setSelectedValue(value))
    setSelectedDropdownValue(value)
    dispatch(setLmsFormMode('none'))
    dispatch(setLmsItem(value))
  }
  const generateSubMenuItems = (
    options: { value: string; label: string }[]
  ) => {
    const valueToLinkMap: Record<string, string> = {
      [MainMenuConst?.radiologist]: radilogist,
      [MainMenuConst?.pathologist]: pathologist,
      [MainMenuConst?.deviceMappings]: proRithmDashboard,
      [MainMenuConst?.labDevices]: labDevices,
      [MainMenuConst?.radiologyDevices]: radiologyDevices,
      [subdropdownitems?.doctor]: doctor,
      [subdropdownitems?.referredOut]: partners,
      [subdropdownitems?.labDeviceModel]: labDeviceModel,
      [subdropdownitems?.parameterMappings]: parameterMappings,
    }
    return options?.map((option) => {
      const link =
        valueToLinkMap?.[option?.value] ||
        `/administration/${hoveredDropdownItem}`
      const handleClick = (ev: any) => {
        ev.preventDefault()
        if (option?.value in valueToLinkMap) {
          window.location.href = valueToLinkMap?.[option?.value]
        } else {
          handleChange(ev)
        }
      }
      return {
        name: option?.value,
        label: option?.label,
        onclick: handleClick,
        link,
      }
    })
  }

  const menuItems: MenuItem[] = [
    {
      name: 'patients',
      label: 'Registration',
      link: '/patients',
      dropdownItems: [
        {
          name: 'membership',
          label: 'Membership',
          link: '/membership',
          roles: [MEMBERSHIP_READ],
        },
        {
          name: 'package',
          label: 'Package',
          link: '/package',
          roles: [PACKAGES_READ],
        },
        {
          name: 'AB Membership',
          label: 'AB Membership',
          link: '/ab/membership',
          roles: [MEMBERSHIP_READ],
        },
        {
          name: 'Order',
          label: 'Order',
          link: '/orderinvoice',
          roles: [PATIENTS_READ],
        },
        {
          name: 'Patient Referrals',
          label: 'Patient Referrals',
          link: '/referralDashboard',
          roles: [PATIENTS_READ],
        },
      ],
      roles: hasRole([PATIENTS_READ, PATIENTS_WRITE])
        ? [PATIENTS_READ, PATIENTS_WRITE]
        : undefined,
    },
    {
      name: 'OPD',
      label: 'OPD',
      link: '/opd',
      dropdownItems: [
        {
          name: 'cancerScreening',
          label: 'Cancer Screening',
          link: '/cancerScreeningDashboard',
          roles: [OPD_READ],
        },
      ],
      roles: [OPD_READ],
    },
    {
      name: 'appointments',
      label: 'Appointments',
      link: '/appointments',
      dropdownItems: [
        {
          name: 'calendar',
          label: 'Calendar',
          link: 'appointments/calendar',
          roles: [CALENDAR_READ],
        },
        {
          name: 'slotManagement',
          label: 'Slot Management',
          link: 'appointments/slot-management',
          roles: [SLOTS_READ],
        },
      ],
      roles: hasRole([APPOINTMENTS_READ]) ? [APPOINTMENTS_READ] : undefined,
    },
    {
      name: 'laboratory',
      label: 'Laboratory',
      link: '/lms',
      dropdownItems: [
        {
          name: 'referredOut',
          label: 'Referred Out',
          link: '/lms/referred',
          roles: [LMS_READ],
        },
        {
          name: 'Trim PDF',
          label: 'Trim PDF',
          link: '/lms/trimPdf',
          roles: [LMS_ADMIN_WRITE],
        },
        {
          name: 'Lab Test Reference',
          label: 'Lab Test Reference',
          link: '/labReference',
          roles: [LMS_READ],
        },
      ],
      roles: hasRole([LMS_READ]) ? [LMS_READ] : undefined,
    },
    {
      name: 'radiology',
      label: 'Radiology',
      link: '/radiology',
      dropdownItems: [
        {
          name: 'referredOut',
          label: 'Referred Out',
          link: '/radiology/referred',
          roles: [REFERRED_OUT],
        },
        {
          name: 'Trim PDF',
          label: 'Trim PDF',
          link: '/radiology/trimPdf',
          roles: [RADIOLOGY_READ],
        },
      ],
      roles: hasRole([RADIOLOGY_WRITE]) ? [RADIOLOGY_WRITE] : undefined,
    },
    {
      name: 'ipd',
      label: 'IPD',
      link: '/bedIpd/bedAssignmentList',
      dropdownItems: [
        {
          name: 'IPRegistration',
          label: 'IP Registration',
          link: '/ipdRegistration',
          roles: [IPD_READ],
        },
        {
          name: 'Bed Allocation',
          label: 'Bed Allocation',
          link: '/bedIpd/bedOverview',
          roles: [IPD_READ],
        },
        {
          name: 'bedManagement',
          label: 'Bed Dashboard',
          link: '/bedIpd/bedManagementDashboard',
          roles: [IPD_READ],
        },
        {
          name: 'Bed Layout',
          label: 'Bed Layout',
          link: '/bedIpd/bedLayout',
          roles: [IPD_READ],
        },
        // {
        //   name: 'Financial Counselling',
        //   label: 'Financial Counselling',
        //   link: '/financialCounselling',
        //   roles: [IPD_READ],
        // },
      ],
      roles: hasRole([IPD_READ]) ? [IPD_READ] : undefined,
    },
    {
      name: 'billing',
      label: 'Billing',
      link: BillingUrl,
      dropdownItems: [
        {
          name: 'pharmacy',
          label: 'Pharmacy',
          link: pharmacyUrl,
          isExternal: true,
          roles: [PATIENTS_READ],
        },
      ],
      roles: hasRole([PATIENTS_READ, PATIENTS_WRITE])
        ? [PATIENTS_READ, PATIENTS_WRITE]
        : undefined,
    },
    {
      name: 'Apps',
      label: 'Apps',
      link: '/apps',
      dropdownItems: [
        {
          name: 'Command Center',
          label: 'Command Center',
          link: COMMANDCENTER_URL,
          isExternal: true,
          roles: [APPS],
        },
        // {
        //   name: 'emp 360',
        //   label: 'Emp 360',
        //   link: REACT_APP_EMP360,
        //   isExternal: true,
        // },
        {
          name: 'Innomaint',
          label: 'Innomaint-Asset Mgmt',
          link: INNOMAINT_URL,
          isExternal: true,
          roles: [APPS],
        },
        {
          name: 'Innomaint - QR Code Generator',
          label: 'Innomaint - QR Code Generator',
          link: INNOMAINT_URL_QR_CODE,
          isExternal: true,
          roles: [APPS],
        },
        {
          name: 'Reporting',
          label: 'Apache superset',
          link: REPORT_URL,
          isExternal: true,
          roles: [APPS],
        },
      ],
      roles: [APPS],
    },
    {
      name: 'administration',
      label: 'Administration',
      link: '/administration',
      dropdownItems: [
        {
          name: 'bedManagementAdmin',
          label: 'Bed Management',
          link: '/administration/bedManagementAdmin',
          subMenuItems: generateSubMenuItems(options),
          roles: [ADMIN_ROLES?.BED_MANAGEMENT_READ],
        },
        {
          name: 'Company',
          label: 'Company',
          link: 'company',
          roles: [ADMIN_ROLES?.COMPANY_READ],
        },
        {
          name: 'departments',
          label: 'Departments',
          link: '/administration/departments',
          roles: [ADMIN_ROLES?.DEPARTMENTS_READ],
        },
        {
          name: 'devices',
          label: 'Devices',
          link: '/devices',
          subMenuItems: generateSubMenuItems(options),
          roles: [ADMIN_ROLES?.DEVICES_ADMIN_READ],
        },
        {
          name: 'laboratory',
          label: 'Laboratory',
          link: '/administration/laboratory',
          subMenuItems: generateSubMenuItems(options),
          roles: [ADMIN_ROLES?.LABORATORY_ADMIN_READ],
        },
        {
          name: 'location',
          label: 'Operating Unit',
          link: '/administration/locations',
          roles: [ADMIN_ROLES?.LOCATION_ADMIN_READ],
        },
        {
          name: 'partners',
          label: 'Partners',
          link: '/administration/bedManagementAdmin',
          subMenuItems: generateSubMenuItems(options),
          roles: [ADMIN_ROLES?.PARTNER_READ],
        },
        {
          name: 'radiology',
          label: 'Radiology',
          link: '/administration/radiology',
          subMenuItems: generateSubMenuItems(options),
          roles: [ADMIN_ROLES?.RADIOLOGY_ADMIN_READ],
        },
        {
          name: 'referredHospitals',
          label: 'Referred Hospitals',
          link: '/administration/referredHospitals',
          roles: [ADMIN_ROLES?.USER_MANAGEMENT_ADMIN_READ],
        },
        {
          name: 'User Management New',
          label: 'User Management',
          link: '/userdata',
          roles: [ADMIN_ROLES?.USER_MANAGEMENT_ADMIN_READ],
        },
      ],
      roles: hasRole([ADMINISTRATION_READ]) ? [ADMINISTRATION_READ] : undefined,
    },
    {
      name: 'OPD Patient View',
      label: 'Camps',
      link: '/listOfCamps',
      dropdownItems: [],
      roles: [CAMP_VIEW_READ],
    },
    // {
    //   name: 'Smart Queue System',
    //   label: 'Smart Queue System',
    //   link: '/smartQueueSystem',
    //   dropdownItems: [],
    //   roles: [OPD_READ],
    // },
    {
      name: 'homekare',
      label: 'HomeKare',
      link: '/homekare',
      roles: [ADMINISTRATION_READ],
    },
    {
      name: 'Patient Referrals',
      label: 'Patient Referrals',
      link: '/referralDashboard',
      dropdownItems: [
        {
          name: 'referredHospitals',
          label: 'Referred Hospitals',
          link: '/referredHospitals',
          roles: [PATIENTS_READ],
        },
      ],
      roles: [PATIENTS_READ],
    },
  ]

  const getPermittedDropdownItems = (dropdownItems: any[] = []) =>
    dropdownItems?.filter((item) => KeycloakService.hasRole(item?.roles || []))

  // Helper function to check if a menu item should be active
  const isMenuItemActive = (itemName: string, itemLink: string): boolean => {
    const routeMapping = MENU_ROUTE_MAPPINGS[itemName]

    if (routeMapping) {
      // Check if current path matches any of the mapped routes
      return routeMapping.some((route) => {
        // Exact match
        if (location.pathname === route) return true
        // Prefix match (for routes that should match children)
        if (location.pathname.startsWith(route + '/')) return true
        return false
      })
    }

    // Default behavior: exact match only
    return location.pathname === itemLink
  }

  return (
    <div className='sideBar'>
      <ul className='sidebarMenu'>
        {menuItems?.map((item) => {
          const hasMainRole = KeycloakService.hasRole(item?.roles || [])
          const permittedDropdownItems = getPermittedDropdownItems(
            item?.dropdownItems
          )
          if (!hasMainRole && permittedDropdownItems?.length === 0) return null
          return (
            <li
              key={item?.name}
              onMouseEnter={(e) =>
                handleMouseEnter(
                  item?.name,
                  e?.currentTarget?.offsetTop + e.currentTarget?.offsetHeight,
                  e?.currentTarget?.offsetLeft
                )
              }
              onMouseLeave={handleMouseLeave}
            >
              {item?.name === MainMenuConst?.billing ? (
                <a href={item?.link} target='_blank' rel='noopener noreferrer'>
                  {item?.label}
                </a>
              ) : (
                <NavLink
                  to={item?.link}
                  className={({ isActive }) =>
                    isMenuItemActive(item?.name, item?.link) || isActive
                      ? 'active'
                      : ''
                  }
                >
                  {item?.label}
                </NavLink>
              )}
              {hoveredItem === item?.name &&
                permittedDropdownItems?.length > 0 && (
                  <div
                    className='location-dropdown'
                    style={{
                      top: dropdownPosition.top,
                      left: dropdownPosition.left,
                    }}
                  >
                    <ul className='location-dropdown-list'>
                      {permittedDropdownItems?.map((dropdownItem) => (
                        <li
                          key={dropdownItem?.name}
                          className='dropdown-item'
                          onMouseEnter={(e) =>
                            handleDropdownItemEnter(dropdownItem?.name, e)
                          }
                          onMouseLeave={handleDropdownItemLeave}
                        >
                          {dropdownItem?.isExternal ? (
                            <a
                              href={dropdownItem?.link}
                              target='_blank'
                              rel='noopener noreferrer'
                              className={`dropdown-link ${
                                hoveredDropdownItem === dropdownItem?.name
                                  ? 'hovered'
                                  : ''
                              }`}
                            >
                              {dropdownItem?.label}
                            </a>
                          ) : (
                            <NavLink
                              to={dropdownItem?.link}
                              className={`dropdown-link ${
                                hoveredDropdownItem === dropdownItem?.name
                                  ? 'hovered'
                                  : ''
                              }`}
                            >
                              <span className='submenu-arrow'>
                                {dropdownItem?.label}
                              </span>
                              {dropdownItem?.subMenuItems && (
                                <span className='arrow'>&#x25B6;</span>
                              )}
                            </NavLink>
                          )}
                          {dropdownItem?.subMenuItems &&
                            hoveredDropdownItem === dropdownItem?.name && (
                              <div
                                ref={(el) =>
                                  (subdropdownRef.current[dropdownItem?.name] =
                                    el)
                                }
                                className='sub-dropdown'
                              >
                                <ul className='sub-dropdown-list'>
                                  {dropdownItem?.subMenuItems?.map(
                                    (subItem: any) => (
                                      <li
                                        key={subItem?.name}
                                        className='sub-dropdown-item'
                                      >
                                        <NavLink
                                          to={subItem?.link}
                                          className='sub-dropdown-link'
                                          onClick={() =>
                                            handleChange({
                                              target: {
                                                value: subItem?.name,
                                              },
                                            } as ChangeEvent<HTMLSelectElement>)
                                          }
                                        >
                                          {subItem?.label}
                                        </NavLink>
                                      </li>
                                    )
                                  )}
                                </ul>
                              </div>
                            )}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default MainMenu
